/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WorkspaceInsightsPage.tsx
 *     Feature         :   217-insights-dashboards (T011 / US1, FR-001)
 *     Description     :   Workspace Insights — what exists, how it's split (client vs
 *                         shared, by capacity, by type), how it grew, what's big, and
 *                         what's empty dead-weight. Rebuilt 2026-07-06 (owner review):
 *                         the old activity/dormancy framing was dropped because
 *                         last_activity is not collected; everything now renders from
 *                         real signals only (FR-014). Per-workspace CU utilization is
 *                         genuinely not collected (it's a capacity-level metric).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { Building, Info } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from 'recharts';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, Select, Input, Checkbox, StatCard, AppTooltip,
  ErrorState, LoadingState, NotCollected, Extended, QuickGlance, chartTooltipStyle,
} from '../../components/ui';
import { UnmeasuredSizeTag } from '../../utils/unmeasuredSize';
import type { DataTableColumn } from '../../components/ui';
import { WorkspaceInventoryModal } from './WorkspaceInventoryModal';

const TYPE_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ec4899'];

interface WorkspaceRow {
  workspaceId: string;
  name: string;
  workspaceType: string | null;
  capacityName: string | null;
  clientName: string | null;
  isClientAssigned: boolean;
  lakehouseCount: number;
  warehouseCount: number;
  pipelineCount: number;
  notebookCount: number;
  semanticModelCount: number;
  reportCount: number;
  totalItems: number;
  storageGb: number;
  /** Lakehouses and warehouses in storageGb the last collection did not size (counted at their last known size). */
  sizeNotMeasuredCount?: number;
}

interface WorkspaceInsightsPayload {
  kpis: {
    totalWorkspaces: number;
    clientAssignedCount: number;
    sharedCount: number;
    emptyCount: number;
    withSemanticModels: number;
    avgItemsPerWorkspace: number | null;   // null over no workspaces (Spec 298 FR-004)
  };
  cumulativeGrowth60d: Array<{ date: string; count: number }>;
  byCapacity: Array<{ capacityName: string; workspaceCount: number }>;
  byType: Array<{ workspaceType: string; count: number }>;
  clientVsShared: { clientAssigned: number; shared: number };
  largestByStorage: Array<{ workspaceId: string; name: string; capacityName: string | null; storageGb: number; sizeNotMeasuredCount?: number }>;
  emptyWorkspaces: Array<{ workspaceId: string; name: string; capacityName: string | null; workspaceType: string | null }>;
  distribution: WorkspaceRow[];
}

const ALL = '__all__';

export const WorkspaceInsightsPage: React.FC = () => {
  const query = useQuery<WorkspaceInsightsPayload>({
    queryKey: ['workspace-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: WorkspaceInsightsPayload }>('/api/workspace-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  // Distribution filters
  const [search, setSearch] = useState('');
  const [capacityFilter, setCapacityFilter] = useState<string>(ALL);
  const [assignmentFilter, setAssignmentFilter] = useState<string>(ALL);
  const [clientFilter, setClientFilter] = useState<string>(ALL);
  const [onlyWithModels, setOnlyWithModels] = useState(false);
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [inventoryWsId, setInventoryWsId] = useState<string | null>(null);

  const capacityOptions = useMemo(
    () => (data?.byCapacity ?? []).map((c) => c.capacityName),
    [data?.byCapacity],
  );

  const clientOptions = useMemo(() => {
    const names = new Set<string>();
    for (const w of data?.distribution ?? []) {
      if (w.isClientAssigned && w.clientName) names.add(w.clientName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [data?.distribution]);

  const filteredDistribution = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.distribution.filter((w) => {
      if (q && !w.name.toLowerCase().includes(q) && !(w.clientName ?? '').toLowerCase().includes(q)) return false;
      if (capacityFilter !== ALL && (w.capacityName ?? 'No capacity') !== capacityFilter) return false;
      if (assignmentFilter === 'client' && !w.isClientAssigned) return false;
      if (assignmentFilter === 'shared' && w.isClientAssigned) return false;
      if (clientFilter !== ALL && w.clientName !== clientFilter) return false;
      if (onlyWithModels && w.semanticModelCount === 0) return false;
      if (onlyEmpty && w.totalItems > 0) return false;
      return true;
    });
  }, [data, search, capacityFilter, assignmentFilter, clientFilter, onlyWithModels, onlyEmpty]);

  const filtersActive =
    search.trim() !== '' || capacityFilter !== ALL || assignmentFilter !== ALL ||
    clientFilter !== ALL || onlyWithModels || onlyEmpty;
  const clearFilters = () => {
    setSearch('');
    setCapacityFilter(ALL);
    setAssignmentFilter(ALL);
    setClientFilter(ALL);
    setOnlyWithModels(false);
    setOnlyEmpty(false);
  };

  const distributionColumns: DataTableColumn<WorkspaceRow>[] = [
    {
      key: 'name', header: 'Workspace', width: '24%',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">{r.name}</div>
          <div className="truncate text-xs text-[var(--color-text-secondary)]">{r.workspaceType ?? '—'}</div>
        </div>
      ),
    },
    { key: 'capacityName', header: 'Capacity', width: '16%', render: (r) => r.capacityName ?? <span className="text-[var(--color-text-secondary)]">—</span> },
    {
      key: 'clientName', header: 'Client', width: '16%',
      render: (r) => (r.isClientAssigned ? (r.clientName ?? '—') : <span className="text-[var(--color-text-secondary)]">shared</span>),
    },
    { key: 'totalItems', header: 'Items', width: '11%', align: 'right', render: (r) => r.totalItems.toLocaleString() },
    { key: 'semanticModelCount', header: 'Models', width: '11%', align: 'right' },
    {
      key: 'storageGb', header: 'Storage (GB)', width: '15%', align: 'right',
      // Spec 286 T159 item 32: the sum counts items the last collection did not size at their last known size.
      render: (r) => (
        <>
          {r.storageGb > 0 ? r.storageGb.toLocaleString() : <span className="text-[var(--color-text-secondary)]">0</span>}
          <UnmeasuredSizeTag count={r.sizeNotMeasuredCount} />
        </>
      ),
    },
  ];

  useAutoRefresh(refresh, { lastFetchedAt });

  return (
    <PageLayout
      title="Workspace Insights"
      description="What exists, how it's split across capacities and clients, how it grew, what's big, and what's empty"
      icon={<Building className="h-6 w-6" />}
      helpId="insights.workspace"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load workspace insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-6">
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Workspaces" value={data.kpis.totalWorkspaces} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="All active workspaces in the tenant (dbo.workspaces, is_active) — the denominator for every split below.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Client-assigned" value={data.kpis.clientAssignedCount} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Workspaces assigned to a specific client via client_workspace_assignments. The rest are shared/central (not tied to one client).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Shared" value={data.kpis.sharedCount} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Central/shared workspaces not tied to a single client. Total Workspaces = Client-assigned + Shared.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Empty (0 items)" value={data.kpis.emptyCount} valueClassName={data.kpis.emptyCount > 0 ? 'text-[var(--color-status-warning)]' : undefined} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Workspaces with no Fabric items — the real archival / dead-weight candidates (mostly empty Personal 'My Workspace' spaces). Counted entity-first from dbo.fabric_items across all six item types.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="With Semantic Models" value={data.kpis.withSemanticModels} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Workspaces containing at least one semantic model (Power BI dataset).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Avg Items / Workspace" value={data.kpis.avgItemsPerWorkspace ?? 'no workspaces'} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Average Fabric items per workspace across all six item types — lakehouses, warehouses, pipelines, notebooks, semantic models, reports (dbo.fabric_items ÷ workspaces).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for growth, distribution charts, and the workspace explorer.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              {/* Growth + client-vs-shared */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Cumulative workspace growth — 60 days</h2>
                    <AppTooltip title="Running total of active workspaces by the date each was first tracked. The curve's slope shows whether new-workspace creation is speeding up or leveling off. (Dates reflect when FabricControl first discovered a workspace — Fabric does not expose a true creation timestamp.)">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  {data.cumulativeGrowth60d.length > 1 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.cumulativeGrowth60d}>
                          <defs>
                            <linearGradient id="wsGrowthFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={45} allowDecimals={false} />
                          <Tooltip {...chartTooltipStyle} />
                          <Area type="monotone" dataKey="count" name="Workspaces" stroke="var(--color-accent)" strokeWidth={2} fill="url(#wsGrowthFill)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <NotCollected reason="Fewer than 2 days of workspace history collected in the 60-day window" />
                  )}
                </Card>

                <Card>
                  <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">Client-assigned vs shared</h2>
                  <div className="mb-4 grid grid-cols-2 gap-4">
                    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
                      <div className="text-2xl font-semibold text-[var(--color-text-primary)]">{data.clientVsShared.clientAssigned}</div>
                      <div className="text-xs text-[var(--color-text-secondary)]">Client-assigned</div>
                    </div>
                    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
                      <div className="text-2xl font-semibold text-[var(--color-text-primary)]">{data.clientVsShared.shared}</div>
                      <div className="text-xs text-[var(--color-text-secondary)]">Shared / central</div>
                    </div>
                  </div>
                  <h3 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">By workspace type</h3>
                  <div className="h-28">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.byType} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="workspaceType" tick={{ fontSize: 10 }} width={90} />
                        <Tooltip {...chartTooltipStyle} />
                        <Bar dataKey="count" name="Workspaces">
                          {data.byType.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>

              {/* Workspaces by capacity + largest by storage */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">Workspaces by capacity</h2>
                  <div style={{ height: Math.max(200, data.byCapacity.length * 26) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.byCapacity} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="capacityName" tick={{ fontSize: 10 }} width={150} />
                        <Tooltip {...chartTooltipStyle} />
                        <Bar dataKey="workspaceCount" name="Workspaces" fill="var(--color-accent)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Largest workspaces by storage</h2>
                    <AppTooltip title="Total storage footprint = sum of lakehouse + warehouse sizes in each workspace. Per-workspace CU utilization is not shown because Fabric reports CU at the capacity level, not per workspace — see Capacity Insights for utilization.">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  {data.largestByStorage.length === 0 ? (
                    <NotCollected reason="No workspace storage sizes collected yet" />
                  ) : (
                    <div style={{ height: Math.max(200, data.largestByStorage.length * 24) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.largestByStorage} layout="vertical" margin={{ left: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} unit=" GB" />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(v, _name, item) => {
                              const n = Number((item?.payload as { sizeNotMeasuredCount?: number } | undefined)?.sizeNotMeasuredCount) || 0;
                              const note = n > 0 ? ` (${n} not measured on the last collection, counted at their last known size)` : '';
                              return [`${Number(v).toLocaleString()} GB${note}`, 'Storage'];
                            }}
                          />
                          <Bar dataKey="storageGb" name="Storage (GB)" fill="var(--color-accent)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Card>
              </div>

              {/* Empty workspaces — dead weight */}
              {data.emptyWorkspaces.length > 0 && (
                <Card padding="none">
                  <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Empty workspaces ({data.emptyWorkspaces.length})</h2>
                  <p className="px-4 text-xs text-[var(--color-text-secondary)]">Zero collected items — archival / cleanup candidates (mostly personal spaces)</p>
                  <DataTable
                    columns={[
                      { key: 'name', header: 'Workspace', width: '46%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
                      { key: 'workspaceType', header: 'Type', width: '27%', render: (r) => r.workspaceType ?? '—' },
                      { key: 'capacityName', header: 'Capacity', width: '27%', render: (r) => r.capacityName ?? '—' },
                    ] as DataTableColumn<WorkspaceInsightsPayload['emptyWorkspaces'][number]>[]}
                    rows={data.emptyWorkspaces}
                    rowKey={(r) => r.workspaceId}
                    pageSize={10}
                    emptyMessage="No empty workspaces"
                  />
                </Card>
              )}

              {/* Filterable workspace explorer */}
              <Card padding="none">
                <div className="px-4 pt-4">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Workspace explorer</h2>
                  <p className="text-xs text-[var(--color-text-secondary)]">Click a workspace for its full inventory — every item, size, and detail.</p>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="w-56">
                      <Input placeholder="Search name or client…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search workspaces" />
                    </div>
                    <div className="w-52">
                      <Select label="Capacity" selectSize="sm" value={capacityFilter} onChange={(e) => setCapacityFilter(e.target.value)}>
                        <option value={ALL}>All capacities</option>
                        {capacityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </div>
                    <div className="w-44">
                      <Select label="Assignment" selectSize="sm" value={assignmentFilter} onChange={(e) => setAssignmentFilter(e.target.value)}>
                        <option value={ALL}>All</option>
                        <option value="client">Client-assigned</option>
                        <option value="shared">Shared / central</option>
                      </Select>
                    </div>
                    <div className="w-52">
                      <Select label="Client" selectSize="sm" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                        <option value={ALL}>All clients</option>
                        {clientOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </div>
                    <label className="flex items-center gap-2 pb-1.5 text-sm text-[var(--color-text-primary)]">
                      <Checkbox checked={onlyWithModels} onChange={(e) => setOnlyWithModels(e.target.checked)} />
                      Has semantic models
                    </label>
                    <label className="flex items-center gap-2 pb-1.5 text-sm text-[var(--color-text-primary)]">
                      <Checkbox checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} />
                      Empty only
                    </label>
                    <span className="pb-1.5 text-xs text-[var(--color-text-secondary)]">
                      Showing <span className="font-semibold text-[var(--color-text-primary)]">{filteredDistribution.length}</span> of {data.distribution.length}
                      {filtersActive && <span className="text-[var(--color-status-warning)]"> · filtered</span>}
                    </span>
                    {filtersActive && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="pb-1.5 text-xs font-medium text-[var(--color-accent)] hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>
                <DataTable columns={distributionColumns} rows={filteredDistribution} rowKey={(r) => r.workspaceId} pageSize={10} onRowClick={(r) => setInventoryWsId(r.workspaceId)} emptyMessage="No workspaces match the filters" />
              </Card>
            </div>
          </Extended>
        </>
      )}
      <WorkspaceInventoryModal
        workspaceId={inventoryWsId}
        onClose={() => setInventoryWsId(null)}
        workspaceIds={filteredDistribution.map((r) => r.workspaceId)}
        onNavigate={setInventoryWsId}
      />
    </PageLayout>
  );
};

export default WorkspaceInsightsPage;
